/**
 * Loot on the ground (docs/handoff/newdir/ITEMS.md): what a monster drops where it falls, as data. Each entry rolls
 * on its own when the monster is killed (the monster's own `loot` list first, data/monsters.ts, then its table
 * here); what drops lies on the ground with a glint of its rarity until the player walks over it or presses the
 * pick-up key, and fades out after `LOOT.ttl` seconds. The rules are systems/items.ts.
 */
import { DUNGEON_DROPS } from './dungeon';
import { RARITIES, type Rarity } from './items';

/** One roll of a drop table: an equipment item, or a stack of goods (data/professions.ts) of min–max. */
export type DropEntry =
  | { readonly item: string; readonly chance: number }
  | { readonly good: string; readonly chance: number; readonly n: readonly [number, number] };

/** Drop tables by monster id (data/monsters.ts), after the monster's own `loot`. */
export const DROP_TABLES: Readonly<Record<string, readonly DropEntry[]>> = {
  wolf: [
    { good: 'wolf-meat', chance: 0.6, n: [1, 2] },
    { item: 'cotton-trousers', chance: 0.05 },
    { item: 'wolf-greaves', chance: 0.03 },
  ],
  snowwolf: [
    { good: 'wolf-meat', chance: 0.6, n: [1, 3] },
    { item: 'wolf-greaves', chance: 0.06 },
  ],
  bandit: [
    { good: 'rice', chance: 0.3, n: [1, 3] },
    { item: 'straw-sandals', chance: 0.06 },
    { item: 'iron-darts', chance: 0.05 },
  ],
  knifer: [
    { item: 'iron-darts', chance: 0.08 },
    { item: 'cloud-boots', chance: 0.02 },
  ],
  assassin: [
    { item: 'plum-needles', chance: 0.04 },
    { item: 'cloud-boots', chance: 0.04 },
    { item: 'bronze-ring', chance: 0.04 },
    { good: 'hone-dust', chance: 0.15, n: [1, 2] },
  ],
  monk: [
    { item: 'manual-cloud-palm', chance: 0.03 },
    { item: 'jade-amulet', chance: 0.02 },
    { item: 'plain-pendant', chance: 0.04 },
    { good: 'gem-pearl-1', chance: 0.05, n: [1, 1] },
  ],
  sparrer: [],
  bear: [
    { good: 'hard-wood', chance: 0.2, n: [1, 1] },
    { item: 'jade-amulet', chance: 0.03 },
    { good: 'gem-amber-1', chance: 0.05, n: [1, 1] },
  ],
  boar: [{ good: 'cabbage', chance: 0.3, n: [1, 2] }],
  viper: [{ good: 'herb-leaf', chance: 0.4, n: [1, 2] }],
  spider: [
    { item: 'plum-needles', chance: 0.03 },
    { good: 'gem-sapphire-1', chance: 0.05, n: [1, 1] },
  ],
  leopard: [
    { item: 'cloud-boots', chance: 0.04 },
    { good: 'gem-ruby-1', chance: 0.05, n: [1, 1] },
  ],
  boss: [
    { item: 'tiger-tally', chance: 0.25 },
    { item: 'jade-ring', chance: 0.15 },
    { good: 'reroll-stone', chance: 0.5, n: [1, 2] },
  ],
  worldboss: [
    { item: 'tiger-tally', chance: 0.5 },
    { item: 'cloud-pendant', chance: 0.25 },
    { good: 'seal-mark', chance: 0.5, n: [1, 1] },
  ],
  // ถ้ำคลื่นคำราม's people (data/dungeon.ts); the boss pays from its chest instead.
  ...DUNGEON_DROPS,
};

/** Ground loot's numbers. */
export const LOOT = {
  /** Seconds a drop lies before it is gone (it fades over the last `fade`). */
  ttl: 120,
  fade: 10,
  /** Walking within this many metres of a drop picks it up. */
  walkReach: 0.9,
  /** The pick-up key reaches the nearest drop within this many metres. */
  keyReach: 2.5,
  /** Drops of one kill scatter round where it fell, up to this far (metres). */
  scatter: 1.2,
  /** Most drops lying in a zone at once (the oldest go first). */
  max: 120,
} as const;

/** The glint of a drop by rarity (goods glint as common). */
export const LOOT_GLINT: Readonly<Record<Rarity, string>> = {
  common: '#e8dcc0',
  fine: '#6ad46a',
  rare: '#5aa8ff',
  epic: '#ff8a4a',
  legend: '#ff5a3a',
  mythic: '#e0306a',
};

/**
 * Rarity upgrade (docs/systems/ITEMS_V2.md R8): an equipment drop is its base's rarity, with a chance to come one step
 * better, and a smaller one for a second. The chance grows with the monster's level (ECONOMY: ~10 equipment drops in
 * an hour at ~60 kills, so an upgrade is seen about every hour at level 1 and every half hour near level 10). Field
 * drops stop at `max`; legend and mythic come from bosses, dungeons and the ladder shops.
 */
export const DROP_UPGRADE = {
  /** Chance of one step at level 0, and added per monster level. */
  base: 0.05,
  perLevel: 0.008,
  /** The second step happens with this fraction of the first's chance. */
  second: 0.25,
  max: 'epic' as Rarity,
};

/** The rarity a drop rolls at: one `rng` draw, a second only when the first upgraded. */
export function dropRarity(base: Rarity, level: number, rng: () => number): Rarity {
  const cap = RARITIES.indexOf(DROP_UPGRADE.max);
  let i = RARITIES.indexOf(base);
  if (i >= cap) return base;
  const c = Math.min(0.5, DROP_UPGRADE.base + DROP_UPGRADE.perLevel * Math.max(0, level));
  if (rng() >= c) return base;
  i++;
  if (i < cap && rng() < c * DROP_UPGRADE.second) i++;
  return RARITIES[i]!;
}

/**
 * The wuxia village's drop rows (client v1 hub; kept out of `DROP_TABLES` so the other zones are untouched): a kill
 * rolls the monster's own `loot`, then its row here; the world boss's chest rolls `wb-ape` rares, then `worldboss`.
 */
export const STARTER_DROPS: Readonly<Record<string, readonly DropEntry[]>> = {
  'boar-starter': DROP_TABLES.boar ?? [],
  worldboss: DROP_TABLES.worldboss ?? [],
};
